import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate, ApiError } from '../shared/api';
import { formatMoney, formatNumber, formatDate } from '../shared/formatting';
import { PgRunView, type PgRunStatus } from './pgRunView';

type Locale = 'hr' | 'en';
type Mode = 'pg1' | 'pg2';

type Pg1Article = {
    sku: string;
    item_name: string;
    i1_fine_g: number;
    i1_book_eur: number;
    i1_order_count: number;
    gap_fine_g: number;
    gap_book_eur: number;
    hitno: boolean;
    oldest_date: string | null;
};

type Mint = { id: number; name: string; product_count: number };

/** Artikl iskljucen iz prijedloga na serveru; wholeOrder = izbaci i sve narudzbe koje ga sadrze. */
type ServerExclusion = { sku: string; name: string; wholeOrder: boolean };

type Pg1Result = {
    articles: Pg1Article[];
    unattributed?: unknown;
    unmatched_i1_lines?: number;
    excluded_order_numbers?: string[];
    budget_cut_skus?: string[];
    open_batch?: string | null;
    stock_batch?: number | null;
    spot_per_gram_eur?: number;
    hitno_count?: number;
};

type Pg2Candidate = {
    product_id: number;
    sku: string;
    name_hr: string;
    supplier_partner_id: number;
    supplier_name: string;
    last_purchase_price_eur: number | null;
    last_purchase_date: string | null;
};

type Pg2Row = {
    warehouse_code: string;
    warehouse_name: string;
    weight_class_id: number | null;
    weight_class_code: string;
    target_qty: number;
    stock_qty: number;
    gap_qty: number;
    gap_fine_g: number;
    gap_value_eur: number;
    candidates: Pg2Candidate[];
    chosen: Pg2Candidate | null;
    replaced_manually?: boolean;
    /** N8: prijenos iz akcijskog 211 ide prvi, ostatak dobavljacu. */
    transfer_items?: { product_id: number; sku: string; name_hr: string; qty: number; unit_cost_eur: number | null }[];
    transfer_qty?: number;
    supplier_qty?: number;
};

type Pg2Result = {
    rows: Pg2Row[];
    l2_info?: { policy?: { max_value_eur?: number } } & Record<string, unknown>;
    promo_stock?: { weight_class_id: number; weight_class_code: string | null; available_qty: number; proposed_qty: number; remaining_qty: number }[];
    totals?: Record<string, unknown>;
};

type SavedRun = {
    id: number;
    public_code: string;
    mode: Mode;
    status: PgRunStatus;
    generated_at: string;
    note: string | null;
    line_count: number;
};

/** Run u tijeku bez promjene dulje od `days` dana (GET /gold-cm-planner/runs/stale). */
type StaleRun = {
    id: number;
    public_code: string;
    status: PgRunStatus;
    status_label: string;
    generated_at: string;
    last_activity_at: string;
    last_checked_at: string | null;
    days: number;
    line_count: number;
    qty: number;
};

type StaleAction = 'received' | 'cancelled' | 'in_transit';

const STATUS_HR: Record<PgRunStatus, string> = { draft: 'nacrt', confirmed: 'potvrđen', sent: 'poslan', received: 'primljen', cancelled: 'storniran' };

/**
 * Modul 40.42 - Plan & generate V2 (PG1 + PG2): ulazni ekran iz task.md F6
 * ("Ulazni ekran: izbor nacina rada, PG1 ili PG2") + spremanje traga runa
 * (bi_pg_runs/bi_pg_run_lines, Tonci 15:30 kontrola).
 *
 * N5 (TASK-40-61, Vedran 25.09.2026): spremanje otvara drugi ekran runa
 * (`PgRunView`) s valorizacijom, ispisom i XLSX-om, kao 40.4. Stara
 * komponenta 40.4 se ne preuzima doslovno, jer nosi fazirane isporuke i
 * cash-schedule koji ne odgovaraju PG1/PG2 obliku. N6: kandidat se bira po
 * retku vec ovdje, a u nacrtu i na drugom ekranu.
 */
export function WarehousePlanGenerateV2Module({ locale, token, onBack }: { locale: Locale; token: string | null; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [mode, setMode] = useState<Mode>('pg1');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // PG1 filtri
    const [mintPartnerId, setMintPartnerId] = useState('');
    const [minAgeDays, setMinAgeDays] = useState('');
    const [maxTotalEur, setMaxTotalEur] = useState('');

    // PG2 filtri
    const [metalType, setMetalType] = useState('gold');
    // Lokalni datum (Zagreb), ne UTC (audit 40.x, 4. tablica).
    const [validFrom, setValidFrom] = useState(() => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Zagreb' }));

    const [pg1, setPg1] = useState<Pg1Result | null>(null);
    const [pg2, setPg2] = useState<Pg2Result | null>(null);
    const [excluded, setExcluded] = useState<Set<number>>(new Set());
    const [serverExcluded, setServerExcluded] = useState<ServerExclusion[]>([]);
    const [mints, setMints] = useState<Mint[]>([]);

    useEffect(() => {
        api<{ mints: Mint[] }>('/gold-cm-planner/pg1/mints', token)
            .then((r) => setMints(r.mints ?? []))
            .catch(() => setMints([]));
    }, [token]);

    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [openRunId, setOpenRunId] = useState<number | null>(null);
    const [runs, setRuns] = useState<SavedRun[]>([]);

    const loadRuns = useCallback(() => {
        api<{ runs: SavedRun[] }>(`/gold-cm-planner/runs?mode=${mode}`, token)
            .then((r) => setRuns((r.runs ?? []).slice(0, 15)))
            .catch(() => setRuns([]));
    }, [mode, token]);
    useEffect(() => { if (openRunId === null) loadRuns(); }, [loadRuns, openRunId]);

    const switchMode = (m: Mode) => {
        setMode(m);
        setPg1(null);
        setPg2(null);
        setExcluded(new Set());
        setError(null);
    };

    // PG2 broji robu iz runova u tijeku kao da stize; run koji nitko ne
    // zatvori trajno gusi prijedlog. Zato prije generiranja pitamo za stare.
    const [staleRuns, setStaleRuns] = useState<StaleRun[] | null>(null);
    const [staleDays, setStaleDays] = useState(5);
    const [staleBusy, setStaleBusy] = useState<number | null>(null);

    const loadPrefill = useCallback(async (skipStaleCheck = false) => {
        setLoading(true);
        setError(null);
        try {
            if (mode === 'pg2' && !skipStaleCheck) {
                const stale = await api<{ days: number; runs: StaleRun[] }>('/gold-cm-planner/runs/stale?mode=pg2', token);
                if ((stale.runs ?? []).length > 0) {
                    setStaleDays(stale.days);
                    setStaleRuns(stale.runs);
                    return;
                }
            }
            if (mode === 'pg1') {
                const params = new URLSearchParams();
                if (mintPartnerId.trim()) params.set('mint_partner_id', mintPartnerId.trim());
                if (minAgeDays.trim()) params.set('min_age_days', minAgeDays.trim());
                if (maxTotalEur.trim()) params.set('max_total_eur', maxTotalEur.trim());
                if (serverExcluded.length > 0) {
                    params.set('excluded', JSON.stringify(serverExcluded.map((x) => ({ sku: x.sku, whole_order: x.wholeOrder }))));
                }
                const qs = params.toString();
                const result = await api<Pg1Result>(`/gold-cm-planner/pg1/prefill${qs ? `?${qs}` : ''}`, token);
                setPg1(result);
                setExcluded(new Set());
            } else {
                const params = new URLSearchParams();
                params.set('metal_type', metalType);
                if (validFrom) params.set('valid_from', validFrom);
                const result = await api<Pg2Result>(`/gold-cm-planner/pg2/prefill?${params.toString()}`, token);
                setPg2(result);
                setExcluded(new Set());
            }
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Ucitavanje nije uspjelo.', 'Loading failed.'));
        } finally {
            setLoading(false);
        }
    }, [mode, mintPartnerId, minAgeDays, maxTotalEur, serverExcluded, metalType, validFrom, token, t]);

    const answerStale = async (run: StaleRun, action: StaleAction) => {
        setStaleBusy(run.id);
        setError(null);
        try {
            await mutate(`/gold-cm-planner/runs/${run.id}/stale-check`, token, 'POST', { action });
            const rest = (staleRuns ?? []).filter((r) => r.id !== run.id);
            setStaleRuns(rest.length > 0 ? rest : null);
            loadRuns();
            if (rest.length === 0) void loadPrefill(true);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Spremanje nije uspjelo.', 'Save failed.'));
        } finally {
            setStaleBusy(null);
        }
    };

    const excludeOnServer = (a: Pg1Article) => {
        setServerExcluded((prev) => (prev.some((x) => x.sku === a.sku) ? prev : [...prev, { sku: a.sku, name: a.item_name, wholeOrder: false }]));
    };

    const toggleExcluded = (i: number) => {
        setExcluded((prev) => {
            const next = new Set(prev);
            if (next.has(i)) next.delete(i);
            else next.add(i);
            return next;
        });
    };

    const saveRun = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            if (mode === 'pg1' && pg1) {
                const items = pg1.articles.map((a, i) => ({ ...a, excluded: excluded.has(i) })).filter((a) => !a.excluded);
                const filters = {
                    mint_partner_id: mintPartnerId || null, min_age_days: minAgeDays || null, max_total_eur: maxTotalEur || null,
                    excluded: serverExcluded.map((x) => ({ sku: x.sku, whole_order: x.wholeOrder })),
                };
                const result = await mutate<{ id: number }>('/gold-cm-planner/runs', token, 'POST', {
                    mode: 'pg1',
                    filters,
                    note: note || null,
                    items,
                });
                setOpenRunId(result.id);
                setNote('');
            } else if (mode === 'pg2' && pg2) {
                const items = pg2.rows.map((r, i) => ({ ...r, excluded: excluded.has(i) })).filter((r) => !r.excluded);
                const filters = { metal_type: metalType, valid_from: validFrom };
                const result = await mutate<{ id: number }>('/gold-cm-planner/runs', token, 'POST', {
                    mode: 'pg2',
                    filters,
                    note: note || null,
                    items,
                });
                setOpenRunId(result.id);
                setNote('');
            }
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Spremanje nije uspjelo.', 'Save failed.'));
        } finally {
            setSaving(false);
        }
    }, [mode, pg1, pg2, excluded, serverExcluded, mintPartnerId, minAgeDays, maxTotalEur, metalType, validFrom, note, token, t]);

    const chooseCandidate = (i: number, productId: number) => {
        setPg2((prev) => {
            if (!prev) return prev;
            const rows = prev.rows.map((r, j) => (j === i ? { ...r, chosen: r.candidates.find((c) => c.product_id === productId) ?? r.chosen, replaced_manually: true } : r));
            return { ...prev, rows };
        });
    };

    return (
        <section className="card admin-block">
            {onBack ? <button type="button" className="secondary compact" onClick={onBack} style={{ marginBottom: 8 }}>← {t('Natrag na Skladišno', 'Back to Warehouse')}</button> : null}
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🚚</div>
                <div className="bi-module-hero-text">
                    <h1>40.42 · {t('Plan & generate V2', 'Plan & generate V2')}</h1>
                    <p className="muted">{t('PG1 — narudžbe za klijente (I1 − S1..S4) i PG2 — narudžba za lager (L1 gap po apoenu).', 'PG1 — client orders (I1 − S1..S4) and PG2 — warehouse order (L1 gap per denomination).')}</p>
                </div>
            </div>

            {staleRuns !== null ? (
                <div className="modal-backdrop" role="dialog" aria-modal="true">
                    <div style={{ width: 'min(760px, 96vw)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: '18px 22px', boxShadow: '0 20px 60px rgba(107,79,28,0.28)' }}>
                        <h3 style={{ margin: '0 0 8px' }}>{t(`Runovi u tijeku stariji od ${staleDays} dana`, `Runs in progress older than ${staleDays} days`)}</h3>
                        <p className="muted" style={{ marginTop: 0 }}>{t(
                            'PG2 njihovu robu broji kao da stiže i zato je ne predlaže ponovno. Za svaki run odaberi stanje.',
                            'PG2 counts their goods as arriving and does not propose them again. Choose a state for each run.',
                        )}</p>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>{t('Run', 'Run')}</th>
                                    <th>{t('Status', 'Status')}</th>
                                    <th>{t('Generiran', 'Generated')}</th>
                                    <th>{t('Bez promjene', 'Unchanged')}</th>
                                    <th>{t('Redaka', 'Lines')}</th>
                                    <th>{t('Kom', 'Qty')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {staleRuns.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.public_code}</td>
                                        <td>{locale === 'en' ? r.status : STATUS_HR[r.status] ?? r.status_label}</td>
                                        <td>{formatDate(r.generated_at, locale)}</td>
                                        <td>{r.days} {t('dana', 'days')}</td>
                                        <td>{r.line_count}</td>
                                        <td>{formatNumber(r.qty, locale)}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <button type="button" className="compact" disabled={staleBusy !== null} onClick={() => void answerStale(r, 'received')}>{t('Primljeno', 'Received')}</button>{' '}
                                            <button type="button" className="secondary compact" disabled={staleBusy !== null} onClick={() => void answerStale(r, 'cancelled')}>{t('Otkazano', 'Cancelled')}</button>{' '}
                                            <button type="button" className="secondary compact" disabled={staleBusy !== null} onClick={() => void answerStale(r, 'in_transit')}>{t('Još putuje', 'Still in transit')}</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {error ? <p className="error">{error}</p> : null}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                            <button type="button" className="secondary" disabled={staleBusy !== null} onClick={() => { setStaleRuns(null); void loadPrefill(true); }}>{t('Nastavi bez odgovora', 'Continue without answering')}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            {openRunId !== null ? <PgRunView locale={locale} token={token} runId={openRunId} onBack={() => setOpenRunId(null)} /> : (<>
            <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
                <button type="button" className={mode === 'pg1' ? '' : 'secondary'} onClick={() => switchMode('pg1')}>PG1 — {t('narudžbe klijenata', 'client orders')}</button>
                <button type="button" className={mode === 'pg2' ? '' : 'secondary'} onClick={() => switchMode('pg2')}>PG2 — {t('narudžba za lager', 'warehouse order')}</button>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
                {mode === 'pg1' ? (
                    <>
                        <label>{t('Kovnica', 'Mint')}<br />
                            <select value={mintPartnerId} onChange={(e) => setMintPartnerId(e.target.value)} style={{ minWidth: 180 }}>
                                <option value="">{t('Sve kovnice', 'All mints')}</option>
                                {mints.map((m) => <option key={m.id} value={String(m.id)}>{m.name} ({m.product_count})</option>)}
                            </select>
                        </label>
                        <label>{t('Min. starost (dana)', 'Min. age (days)')}<br />
                            <input value={minAgeDays} onChange={(e) => setMinAgeDays(e.target.value)} style={{ width: 140 }} />
                        </label>
                        <label>{t('Gornja granica (EUR)', 'Upper limit (EUR)')}<br />
                            <input value={maxTotalEur} onChange={(e) => setMaxTotalEur(e.target.value)} style={{ width: 140 }} />
                        </label>
                    </>
                ) : (
                    <>
                        <label>{t('Metal', 'Metal')}<br />
                            <select value={metalType} onChange={(e) => setMetalType(e.target.value)}>
                                <option value="gold">{t('Zlato', 'Gold')}</option>
                                <option value="silver">{t('Srebro', 'Silver')}</option>
                            </select>
                        </label>
                        <label>{t('Vrijedi od', 'Valid from')}<br />
                            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
                        </label>
                    </>
                )}
                <button type="button" onClick={() => void loadPrefill()} disabled={loading}>{loading ? t('Učitavam…', 'Loading…') : t('Učitaj prijedlog', 'Load proposal')}</button>
            </div>

            {mode === 'pg1' && serverExcluded.length > 0 ? (
                <div style={{ marginBottom: '1rem' }}>
                    <strong>{t('Isključeni artikli', 'Excluded items')}</strong>
                    <span className="muted"> {t('(vrijede nakon ponovnog učitavanja prijedloga)', '(applied on next proposal load)')}</span>
                    <table className="table" style={{ marginTop: 4 }}>
                        <thead>
                            <tr>
                                <th>SKU</th>
                                <th>{t('Naziv', 'Name')}</th>
                                <th>{t('Isključi i sve narudžbe s ovim artiklom', 'Also exclude all orders containing it')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {serverExcluded.map((x) => (
                                <tr key={x.sku}>
                                    <td>{x.sku}</td>
                                    <td>{x.name}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className={x.wholeOrder ? 'compact' : 'secondary compact'}
                                            onClick={() => setServerExcluded((prev) => prev.map((y) => (y.sku === x.sku ? { ...y, wholeOrder: !y.wholeOrder } : y)))}
                                        >
                                            {x.wholeOrder ? t('DA', 'YES') : t('NE', 'NO')}
                                        </button>
                                    </td>
                                    <td>
                                        <button type="button" className="secondary compact" onClick={() => setServerExcluded((prev) => prev.filter((y) => y.sku !== x.sku))}>
                                            {t('Vrati', 'Restore')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

            {mode === 'pg1' && pg1 ? (
                <>
                    <p className="muted">
                        {t('Prijedlog', 'Proposal')}: {pg1.articles.length} {t('artikala', 'items')}
                        {typeof pg1.hitno_count === 'number' ? `, ${pg1.hitno_count} ${t('hitno', 'urgent')}` : ''}
                        {pg1.excluded_order_numbers && pg1.excluded_order_numbers.length > 0 ? `, ${pg1.excluded_order_numbers.length} ${t('narudžbi isključeno zbog artikla', 'orders excluded by item')}` : ''}
                        {typeof pg1.unmatched_i1_lines === 'number' && pg1.unmatched_i1_lines > 0 ? `, ${pg1.unmatched_i1_lines} ${t('nespojenih I1 redaka (broje se, ne raspoređuju)', 'unmatched I1 lines (counted, not allocated)')}` : ''}
                    </p>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>SKU</th>
                                    <th>{t('Naziv', 'Name')}</th>
                                    <th>{t('Gap g', 'Gap g')}</th>
                                    <th>{t('Gap EUR', 'Gap EUR')}</th>
                                    <th>{t('Hitno', 'Urgent')}</th>
                                    <th>{t('Najstarija narudžba', 'Oldest order')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pg1.articles.map((a, i) => (
                                    <tr key={a.sku} style={excluded.has(i) ? { opacity: 0.4 } : undefined}>
                                        <td><input type="checkbox" checked={excluded.has(i)} onChange={() => toggleExcluded(i)} title={t('Isključi', 'Exclude')} /></td>
                                        <td>{a.sku}</td>
                                        <td>{a.item_name}</td>
                                        <td>{formatNumber(a.gap_fine_g, locale)}</td>
                                        <td>{formatMoney(a.gap_book_eur, locale)}</td>
                                        <td>{a.hitno ? '🔴' : ''}</td>
                                        <td>{a.oldest_date ? formatDate(a.oldest_date, locale) : ''}</td>
                                        <td>
                                            <button type="button" className="secondary compact" onClick={() => excludeOnServer(a)} title={t('Dobavljač nema artikl: izbaci ga iz prijedloga', 'Supplier lacks the item: drop it from the proposal')}>
                                                {t('Nema kod dobavljača', 'Not available')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}

            {mode === 'pg2' && pg2 ? (
                <>
                    {pg2.promo_stock && pg2.promo_stock.length > 0 ? (
                        <p className="muted">
                            {t('Akcijsko 211, prvo se predlaže prijenos', 'Promo warehouse 211, transfer is proposed first')}:{' '}
                            {pg2.promo_stock.map((p) => `${p.weight_class_code ?? p.weight_class_id} ${formatNumber(p.proposed_qty, locale)}/${formatNumber(p.available_qty, locale)}`).join(' · ')}
                        </p>
                    ) : null}
                    {pg2.l2_info ? (
                        <p className="muted">
                            L2 ({t('zaseban ukupni budžet, izvan formule po apoenu', 'separate overall budget, outside per-denomination formula')}): {typeof pg2.l2_info.policy?.max_value_eur === 'number' ? formatMoney(pg2.l2_info.policy.max_value_eur, locale) : '-'}
                        </p>
                    ) : null}
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>{t('Poslovnica', 'Branch')}</th>
                                    <th>{t('Apoen', 'Denomination')}</th>
                                    <th>{t('Manjak kom', 'Gap qty')}</th>
                                    <th>{t('Iz 211', 'From 211')}</th>
                                    <th>{t('Dobavljaču', 'To supplier')}</th>
                                    <th>{t('Gap EUR', 'Gap EUR')}</th>
                                    <th>{t('Izabrani artikl/dobavljač', 'Chosen item/supplier')}</th>
                                    <th>{t('Zadnja nabavna cijena', 'Last purchase price')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pg2.rows.map((r, i) => (
                                    <tr key={`${r.warehouse_code}-${r.weight_class_id}-${r.weight_class_code}`} style={excluded.has(i) ? { opacity: 0.4 } : undefined}>
                                        <td><input type="checkbox" checked={excluded.has(i)} onChange={() => toggleExcluded(i)} title={t('Isključi', 'Exclude')} /></td>
                                        <td>{r.warehouse_name || r.warehouse_code}</td>
                                        <td>{r.weight_class_code}</td>
                                        <td>{formatNumber(r.gap_qty, locale)}</td>
                                        <td title={(r.transfer_items ?? []).map((x) => `${x.qty} × ${x.sku} ${x.name_hr}`).join('\n')}>{r.transfer_qty ? formatNumber(r.transfer_qty, locale) : ''}</td>
                                        <td>{formatNumber(r.supplier_qty ?? r.gap_qty, locale)}</td>
                                        <td>{formatMoney(r.gap_value_eur, locale)}</td>
                                        <td>
                                            {r.candidates.length > 1 ? (
                                                <select value={r.chosen?.product_id ?? ''} onChange={(e) => chooseCandidate(i, Number(e.target.value))} style={{ maxWidth: 360 }}>
                                                    {r.candidates.map((c) => (
                                                        <option key={c.product_id} value={c.product_id}>
                                                            {c.sku} · {c.name_hr} · {c.supplier_name}{c.last_purchase_price_eur != null ? ` · ${formatMoney(c.last_purchase_price_eur, locale)}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : r.chosen ? `${r.chosen.sku} · ${r.chosen.name_hr} · ${r.chosen.supplier_name}` : <em>{t('bez kandidata', 'no candidate')}</em>}
                                        </td>
                                        <td>{r.chosen?.last_purchase_price_eur != null ? formatMoney(r.chosen.last_purchase_price_eur, locale) : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}

            {pg1 || pg2 ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: '1rem' }}>
                    <label>{t('Napomena', 'Note')}<br />
                        <input value={note} onChange={(e) => setNote(e.target.value)} style={{ width: 320 }} />
                    </label>
                    <button type="button" onClick={saveRun} disabled={saving}>{saving ? t('Spremam…', 'Saving…') : t('Spremi kao nacrt i otvori', 'Save as draft and open')}</button>
                </div>
            ) : null}

            {runs.length > 0 ? (
                <div style={{ marginTop: '1.5rem' }}>
                    <strong>{t('Spremljeni runovi', 'Saved runs')} ({mode.toUpperCase()})</strong>
                    <table className="table" style={{ marginTop: 4 }}>
                        <thead>
                            <tr>
                                <th>{t('Oznaka', 'Code')}</th>
                                <th>{t('Generirano', 'Generated')}</th>
                                <th>{t('Status', 'Status')}</th>
                                <th>{t('Redaka', 'Lines')}</th>
                                <th>{t('Napomena', 'Note')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.public_code}</td>
                                    <td>{formatDate(r.generated_at, locale)} {r.generated_at.slice(11, 16)}</td>
                                    <td>{locale === 'en' ? r.status : STATUS_HR[r.status] ?? r.status}</td>
                                    <td>{r.line_count}</td>
                                    <td>{r.note ?? ''}</td>
                                    <td><button type="button" className="secondary compact" onClick={() => setOpenRunId(r.id)}>{t('Otvori', 'Open')}</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
            </>)}
        </section>
    );
}
